import React, { memo, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { createUseStyles, Modal, Advice, Text, Input } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from 'lib/Web5Context';

import styles from './styles';

const useStyles = createUseStyles(styles, 'DeleteViewModal');

const DeleteViewModal = memo(
    ({ className, labels, isOpen, onDelete, onClose, selectedView, inputAutoFocus = true }) => {
        const classes = useStyles();
        const classNames = classnames(classes.root, className);
        const [value, setValue] = useState('');
        const [isLoading, setIsLoading] = useState(false);
        const { t } = useWeb5Context();

        const onChange = useCallback((newValue) => {
            setValue(newValue);
        }, []);

        const handleOnDelete = useCallback(() => {
            setIsLoading(true);
            onDelete &&
                onDelete(selectedView).finally(() => {
                    setIsLoading(false);
                    onClose && onClose();
                });
        }, [onDelete, selectedView, onClose]);

        const handleOnClose = useCallback(() => {
            if (isLoading) return;
            onClose && onClose();
        }, [onClose, isLoading]);

        const canDelete = useMemo(() => {
            if (value === labels?.confirmWord || value === t('action_delete').toUpperCase()) {
                return true;
            }
            return false;
        }, [value, labels, t]);

        return (
            <Modal
                className={classNames}
                overlayClassName={classes.overlay}
                isOpen={isOpen}
                title={labels?.title || t('action_delete_views')}
                confirmText={labels?.confirmText || t('action_delete')}
                cancelText={labels?.cancelText || t('action_cancel')}
                onConfirm={handleOnDelete}
                onCancel={handleOnClose}
                onRequestClose={handleOnClose}
                useCornerClose={false}
                isConfirmDisabled={isLoading || !canDelete}
                isConfirmLoading={isLoading}
            >
                <div className={classes.content}>
                    <div className={classes.formContainer}>
                        <Advice className={classes.advice} type="error" showIcon>
                            {labels?.adviceText || t('label_delete_view_explanation')}
                        </Advice>
                        <Text className={classes.confirmText} type="body">
                            {labels?.confirmWriteText ||
                                t('label_type_confirmation', [
                                    labels?.confirmWord || t('action_delete').toUpperCase(),
                                ])}
                        </Text>
                        <Input
                            className={classes.input}
                            placeholder={
                                labels?.confirmWritePlaceholder || t('placeholder_text_field')
                            }
                            onChange={onChange}
                            value={value}
                            isFullWidth
                            isReadOnly={isLoading}
                            autoFocus={inputAutoFocus}
                        />
                    </div>
                </div>
            </Modal>
        );
    },
);

DeleteViewModal.propTypes = {
    className: PropTypes.string,
    isOpen: PropTypes.bool,
    onSave: PropTypes.func,
    onClose: PropTypes.func,
    labels: PropTypes.shape({
        title: PropTypes.string,
        confirmText: PropTypes.string,
        cancelText: PropTypes.string,
        questionText: PropTypes.string,
        adviceText: PropTypes.string,
        confirmWriteText: PropTypes.string,
        confirmWord: PropTypes.string,
        confirmWritePlaceholder: PropTypes.string,
    }),
};

export default DeleteViewModal;
